import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import VerificationForm from './VerificationForm';
import VerificationLoader from '../common/VerificationLoader';
import { useAuth } from '../../contexts/AuthContext';
import useOnbaseApi from '../../services/useOnbaseApi';
import { useMetroConfig } from '../../contexts/MetroConfigContext';
import Loader from '../common/SuspenseLoader';
import MetroConfigErrorPage from '../common/MetroConfigErrorPage';
import { useBrand } from '../../theme/BrandProvider';
import { getUserData } from '../../services/userData';
import { getAgentSession } from './verificationApi';
import { processSteps, getConfigValue } from '../../utils/templateInterpolation';

interface VerificationPageProps {
  onVerify?: (pin: string) => Promise<void>;
  isSubmitting?: boolean;
  errorMessage?: string;
  /**
   * Optional docId provided via path param (/:docId). If present it overrides any docId query param.
   * This avoids repeated parsing work downstream – higher level route extracts a sanitized segment.
   */
  docIdFromPath?: string;
}

const VerificationPage: React.FC<VerificationPageProps> = ({
  onVerify,
  isSubmitting = false,
  errorMessage,
  docIdFromPath,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loaderMode, setLoaderMode] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [error, setError] = useState<string | undefined>(errorMessage);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const brand = useBrand();
  const { setIsAuthenticated, isAuthenticated, logout } = useAuth();
  const docId = docIdFromPath;
  const { verifyPin: apiVerifyPin } = useOnbaseApi();
  const { metroConfig, retry: retryMetroConfig } = useMetroConfig();

  // Force refresh metro config (single attempt) when returning from support or initial mount
  const refreshAttemptedRef = React.useRef(false);
  React.useEffect(() => {
    if (refreshAttemptedRef.current) return;
    if (docId && !metroConfig.loading && metroConfig.isLoaded) {
      refreshAttemptedRef.current = true;
      retryMetroConfig();
    }
  }, [docId, metroConfig.loading, metroConfig.isLoaded, retryMetroConfig]);

  // Check if mobile
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Safety: stop loader if component unmounts mid-sequence
  React.useEffect(() => {
    return () => {
      setIsLoading(false);
      setLoaderMode('loading');
    };
  }, []);

  // Determine where to go after auth; default to /welcome if no origin captured
  const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? '/welcome';

  // Derive the final step index from the config-driven step list so it stays in sync
  // with whatever steps the metro config defines.
  const finalStepIndex = React.useMemo(() => {
    const steps = processSteps(getConfigValue(metroConfig?.data, ['configs', 'WelcomeBody', 'steps']));
    return steps.length > 0 ? steps.length - 1 : 6;
  }, [metroConfig?.data]);

  // Helper: Determine navigation destination based on user state
  const getNavigationDestination = React.useCallback((targetStep: number, agentStatus?: string, onBaseStatusCode?: string) => {
    if (targetStep === 0) {
      return { path: fromPath ?? '/welcome', state: undefined };
    }
    
    if (targetStep === finalStepIndex) {
      if (agentStatus === "FAILED") {
        const path = !onBaseStatusCode || `${onBaseStatusCode}`.startsWith('4') 
          ? '/error/packet-failed'
          : '/error/packet-rejected';
        return { path, state: undefined };
      }
      if (agentStatus === "COMPLETED") {
        return { path: '/agent-affiliation', state: { targetStep } };
      }
      // If targetStep is the final step but status is not FAILED or COMPLETED, don't navigate
      return null;
    }
    
    return { path: '/agent-affiliation', state: { targetStep } };
  }, [fromPath, finalStepIndex]);

  // If user is already authenticated, redirect back to intended page
  React.useEffect(() => {
    if (isAuthenticated) {
      const session = getAgentSession(docId);
      if (session) navigate(fromPath ?? '/welcome', { replace: true });
      else logout();
    }
  }, [isAuthenticated, navigate, fromPath, docId, logout]);

  React.useEffect(() => {
    setError(errorMessage);
  }, [errorMessage]);

  const handleVerify = async (pin: string) => {
    setIsLoading(true);
    setLoaderMode("loading");
    setError(undefined);

    try {
      // Ensure we have the required docId from URL
      if (!docId) {
        throw new Error("Missing verification context");
      }

      // Call real API (if it resolves, treat as success regardless of body shape)
      await apiVerifyPin({ docId, pin });
      setLoaderMode('success');
      
      // If custom onVerify is provided, also call it
      if (onVerify) {
        await onVerify(pin);
      }
  } catch (err: unknown) {
      // Trigger error sequence in loader
      setLoaderMode("error");
      // After the animation completes, show the error message and stop loading
  const message = err instanceof Error ? err.message : "Verification failed";
      // Delay the UI error to align with animation end; the loader component will call back
      const handleEnd = () => {
        setError(message);
        setIsLoading(false);
        setLoaderMode("loading");
      };
      // Use a one-off global for this scope via window to bridge callback
  (window as unknown as { __verify_loader_cb__?: () => void }).__verify_loader_cb__ = handleEnd;
    } finally {
      // success path hides via navigation; error handled by callback above
    }
  };

  // Desktop now uses full-screen color expansion via loader, so no card tinting is needed.

  return (
    <>
      {docId && metroConfig.loading && !metroConfig.error && (
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <Loader />
        </div>
      )}
      {docId && !metroConfig.loading && metroConfig.error && (
        <MetroConfigErrorPage
          error={String(metroConfig.error)}
          docId={metroConfig.docId}
          onRetry={() => {
            // Force refetch by clearing cache & triggering retry via context pattern: navigate reload or add a retry prop if needed
            window.location.reload();
          }}
          onContinueWithoutConfig={() => { /* intentionally disabled - config required */ }}
          onGoBack={() => { window.history.back(); }}
        />
      )}
      {docId && !metroConfig.loading && !metroConfig.error && (isMobile ? (
        <div className={`h-screen`} style={{ backgroundColor: brand.theme.colors.background.mobile}}>
          <VerificationForm
            onVerify={handleVerify}
            isLoading={isLoading ?? isSubmitting}
            error={error}
            onEdit={() => setError(undefined)}
          />
        </div>
      ) : (
        <div className="w-full md:flex md:items-center md:justify-center md:min-h-[calc(100vh-64px)]" style={{ backgroundColor: brand.theme.colors.background.mobile}}>
          <div
            id="verification-card"
            className="w-full md:max-w-[540px] md:bg-white md:p-8 mx-auto transition-colors duration-300"
          >
            <VerificationForm
              onVerify={handleVerify}
              isLoading={isLoading ?? isSubmitting}
              error={error}
              onEdit={() => setError(undefined)}
            />
          </div>
        </div>
      ))}
      <VerificationLoader
        isVisible={isLoading ?? isSubmitting}
        isMobile={isMobile}
        mode={loaderMode}
        targetId={!isMobile ? 'verification-card' : undefined}
        // Allow tests to force instant completion by setting a global flag (avoids long animation delays)
  testInstantComplete={Boolean((window as unknown as { __TEST_INSTANT_VERIFY__?: unknown }).__TEST_INSTANT_VERIFY__)}
        onErrorComplete={() => {
          const cb = (window as unknown as { __verify_loader_cb__?: () => void }).__verify_loader_cb__;
          if (cb) {
            cb();
            delete (window as { __verify_loader_cb__?: () => void }).__verify_loader_cb__;
          }
        }}
        onSuccessComplete={() => {
          // The API hook stores the server-issued session before this callback.
          setIsAuthenticated(true);
          setIsLoading(false);
          setLoaderMode('loading');
          
          // Retrieve user data and determine navigation
          const userData = getUserData();
          const targetStep = typeof userData?.pageNumber === 'number' && !Number.isNaN(userData.pageNumber) ? userData.pageNumber : 0;
          const agentStatus = userData?.agentStatus as string | undefined;
          const onBaseStatusCode = userData?.onBaseStatusCode as string | undefined;
          const destination = getNavigationDestination(targetStep, agentStatus, onBaseStatusCode);
          
          if (destination) {
            navigate(destination.path, { replace: true, ...(destination.state && { state: destination.state }) });
          }
        }}
      />
    </>
  );
};

export default VerificationPage;